import { startTransition, StrictMode } from "react";
import { hydrateRoot } from "react-dom/client";
import { HydratedRouter } from "react-router/dom";
import { msalInstance, updateAccount } from "./api";
import { pathname } from "./utils";

let hasInitialized = false;

// This is the React Router v7 equivalent of Gatsby's onClientEntry
async function onClientEntry() {
    hasInitialized = true;

    await msalInstance.initialize();
    const redirectPromise = await msalInstance.handleRedirectPromise();

    // Update the account if we got one from the redirect
    if (redirectPromise?.account) {
        updateAccount(redirectPromise.account);
    }

    // Get the current account state
    const currentAccount = msalInstance.getAllAccounts()[0];
    updateAccount(currentAccount);

    if (
        !currentAccount &&
        !redirectPromise?.account &&
        !pathname?.includes("logout") &&
        !pathname?.includes("inactive")
    ) {
        await msalInstance.loginRedirect();
        return; // Don't continue with hydration if we're redirecting
    }

    // Initialize New Relic AFTER authentication is complete
    try {
        const isNewRelicEnabled = !!import.meta.env.VITE_NEWRELIC_LICENSE_KEY;
        if (!isNewRelicEnabled) {
            if (
                import.meta.env.VITE_ENVIRONMENT === "dev" ||
                import.meta.env.VITE_ENVIRONMENT === "stg"
            ) {
                // Only logged in dev/stg — Vite statically replaces VITE_ENV and
                // tree-shakes this branch out of uat/prod bundles entirely.
                console.error("[NewRelic] Not enabled");
            }
            return;
        }
        if (hasInitialized) {
            const { initializeNewRelic, setupGlobalMonitoring } = await import(
                "./utils/newrelic"
            );
            await initializeNewRelic();
            setupGlobalMonitoring();
        }
    } catch (error) {
        if (
            import.meta.env.VITE_ENVIRONMENT === "dev" ||
            import.meta.env.VITE_ENVIRONMENT === "stg"
        ) {
            // Only logged in dev/stg — Vite statically replaces VITE_ENV and
            // tree-shakes this branch out of uat/prod bundles entirely.
            console.error("[NewRelic] Initialization failed:", error);
        }
    }
}

async function hydrate() {
    // Run our client entry logic first (like Gatsby's onClientEntry)
    await onClientEntry();

    // Then hydrate the app
    startTransition(() => {
        hydrateRoot(
            document,
            <StrictMode>
                <HydratedRouter />
            </StrictMode>,
        );
    });
}

// Handle any errors during initialization
hydrate().catch(console.error);
